import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Faengt Abstuerze beim Zeichnen ab.
 *
 * Ohne diese Klammer raeumt React bei einem Fehler den gesamten Baum ab und
 * hinterlaesst eine weisse Seite - ohne jeden Hinweis, was passiert ist. Genau
 * das ist am 24.08.2026 passiert, als ein Datensatz aelterer Form auf eine neue
 * Ansicht traf. Eine Fehlermeldung, die man vorlesen kann, ist unendlich viel
 * mehr wert als nichts.
 */
interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('Anzeige abgestürzt', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div className="gate">
        <div className="gate-card">
          <img
            className="brand-logo"
            src="/logo.svg"
            alt="WACH"
            style={{ height: 26, marginBottom: 18 }}
          />
          <h1>Da ist die Anzeige abgestürzt</h1>
          <p>
            Deine Daten sind unversehrt – es ist nur die Darstellung, die hier gerade nicht
            zurechtkommt. Nichts wurde gespeichert oder verändert.
          </p>

          <div className="note note-danger" style={{ marginTop: 14 }}>
            <strong>Was schiefging</strong>
            <div className="mono small" style={{ marginTop: 6, wordBreak: 'break-word' }}>
              {error.message || String(error)}
            </div>
          </div>

          <div className="row" style={{ marginTop: 16 }}>
            <button className="btn-primary" onClick={() => window.location.reload()}>
              Neu laden
            </button>
            <button
              onClick={() => {
                void navigator.clipboard
                  .writeText(`${error.message}\n\n${error.stack ?? ''}`)
                  .catch(() => undefined);
              }}
            >
              Fehler kopieren
            </button>
          </div>

          <p className="small muted" style={{ marginTop: 14, marginBottom: 0 }}>
            Hilft das Neuladen nicht, schick den kopierten Text weiter – daraus lässt sich die
            Ursache meist direkt ablesen.
          </p>
        </div>
      </div>
    );
  }
}
